/** Numeric filter parsing with Polish comma support and progressive prefix matching. */

export type NumericOp = "gt" | "gte" | "lt" | "lte" | "eq" | "neq" | "prefix";

export interface NumericFilterValue {
	operator: NumericOp;
	min: number;
	max?: number;
}

const NUMERIC_OPERATORS = [">=", "<=", "<>", "!=", ">", "<", "="] as const;

export function symbolToOp(sym: string): NumericOp {
	if (sym === ">=") return "gte";
	if (sym === "<=") return "lte";
	if (sym === ">") return "gt";
	if (sym === "<") return "lt";
	if (sym === "<>" || sym === "!=") return "neq";
	return "eq";
}

export function opToSymbol(op: string, fallback = "="): string {
	if (op === "gte") return ">=";
	if (op === "lte") return "<=";
	if (op === "gt") return ">";
	if (op === "lt") return "<";
	if (op === "neq") return "<>";
	if (op === "prefix") return "";
	return fallback;
}

/** Normalize decimal comma → dot and strip whitespace. */
function normalizeNumStr(raw: string): string {
	return raw.replace(/,/g, ".").replace(/\s/g, "");
}

/**
 * Parse a user-typed numeric filter string.
 *
 * - Supports operators: `>`, `>=`, `<`, `<=`, `=`, `<>`, `!=`
 * - Supports Polish comma as decimal separator: `26,71`
 * - Without operator: progressive prefix match — `26` → [26,27), `26,7` → [26.7,26.8)
 */
export function parseNumericFilter(raw: string): NumericFilterValue | undefined {
	const trimmed = raw.trim();
	if (!trimmed) return undefined;

	for (const op of NUMERIC_OPERATORS) {
		if (trimmed.startsWith(op)) {
			const num = Number.parseFloat(normalizeNumStr(trimmed.slice(op.length).trim()));
			if (!Number.isNaN(num)) return { operator: symbolToOp(op), min: num };
		}
	}

	// Progressive prefix match: "26" → [26,27), "26,7" → [26.7,26.8), "26,71" → [26.71,26.72)
	const normalized = normalizeNumStr(trimmed);
	const num = Number.parseFloat(normalized);
	if (!Number.isNaN(num)) {
		const dotIdx = normalized.indexOf(".");
		if (dotIdx === -1) {
			return { operator: "prefix", min: num, max: num + 1 };
		}
		const decimals = normalized.length - dotIdx - 1;
		const step = 10 ** -decimals;
		const factor = 10 ** decimals;
		const max = Math.round((num + step) * factor) / factor;
		return { operator: "prefix", min: num, max };
	}

	return undefined;
}

/** Apply a parsed numeric filter to a single value. */
export function matchesNumericFilter(
	value: number | null | undefined,
	filter: NumericFilterValue,
	zeroMeansNull = false,
): boolean {
	if (
		zeroMeansNull &&
		filter.min === 0 &&
		(filter.operator === "eq" || (filter.operator === "prefix" && filter.max === 1))
	) {
		return value == null;
	}
	if (value == null || Number.isNaN(value)) return false;

	switch (filter.operator) {
		case "gt":
			return value > filter.min;
		case "gte":
			return value >= filter.min;
		case "lt":
			return value < filter.min;
		case "lte":
			return value <= filter.min;
		case "eq":
			return value === filter.min;
		case "neq":
			return value !== filter.min;
		case "prefix":
			return filter.max !== undefined
				? value >= filter.min && value < filter.max
				: value >= filter.min;
		default:
			return true;
	}
}
